import React, { useEffect, useState } from 'react';
import { Grid, CircularProgress, styled } from '@mui/material';
import { Chip, Text, TextButton } from 'library';
import { client, consoleLogInDev, formatPhoneNumber } from 'shared';
import { PagingResult } from 'models';
import { formatMonthDayYearWith12HourTime } from 'shared/Dates';
import { Table, TableColumn } from 'library/ui';
import AddUser from './AddUser';
import EditUser from './EditUser';
import { useUserContext } from 'UserContext';
import ConfirmDialog from 'reusableComponents/confirmDialog';

interface IBusinessContactListPermission {
    user: number;
    locations: string;
    permissions: string;
    roles: string;
    email: string;
    phone_number: string;
    first_name: string;
    last_name: string;
    last_login: string;
}

type ContactRow = IBusinessContactListPermission & { id: number; hideActions: boolean };

const SPLIT_CHAR = '||';

export default function ManageUsers() {
    const [contacts, setContacts] = useState<PagingResult<IBusinessContactListPermission>>();
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedContact, setSelectedContact] = useState<IBusinessContactListPermission>();
    const [contactToDelete, setContactToDelete] = useState<IBusinessContactListPermission>();
    const { user } = useUserContext();

    useEffect(() => {
        loadContacts(1);
    }, []);

    function loadContacts(page: number) {
        setIsLoading(true);
        client(`api/business-contact/?page=${page}`)
            .then((res) => setContacts(res))
            .catch(consoleLogInDev)
            .finally(() => setIsLoading(false));
    }

    function onPageChange(newPage: number) {
        setPageNumber(newPage);
        loadContacts(newPage);
    }

    function onContactAdded() {
        loadContacts(pageNumber);
    }

    function onDeleteContact() {
        client(`api/business-contact/delete-contact/`, {
            body: {
                user_id: contactToDelete?.user,
            },
        })
            .then(() => {
                const refreshPage = contacts!.results.length > 1 ? pageNumber : pageNumber - 1;
                loadContacts(refreshPage);
                setPageNumber(refreshPage);
                setContactToDelete(undefined);
            })
            .catch(consoleLogInDev);
    }

    const showCurrentUser = user && user.role !== 'BUSINESS_CONTACT' && pageNumber === 1;
    const tableData: ContactRow[] = [
        ...(showCurrentUser
            ? [
                  {
                      ...user,
                      user: user.id,
                      locations: '',
                      permissions: 'All',
                      roles: '',
                      last_login: '',
                      id: user.id,
                      hideActions: true,
                  } as ContactRow,
              ]
            : []),
        ...(contacts?.results.map((c) => ({ ...c, id: c.user, hideActions: false })) ?? []),
    ];

    const columns: TableColumn<ContactRow>[] = [
        {
            key: 'name',
            label: 'Name (Last Login)',
            render: (row) => (
                <>
                    <Text variant="body2">
                        {row.first_name} {row.last_name}
                    </Text>
                    <Text variant="caption">
                        {row.last_login ? formatMonthDayYearWith12HourTime(row.last_login) : ''}
                    </Text>
                </>
            ),
        },
        {
            key: 'contact',
            label: 'Contact Info',
            render: (row) => (
                <>
                    <Text variant="body2">{row.email}</Text>
                    <Text variant="body2">{row.phone_number ? formatPhoneNumber(row.phone_number) : ''}</Text>
                </>
            ),
        },
        {
            key: 'access',
            label: 'Access',
            render: (row) =>
                row.locations ? (
                    <>
                        {row.locations.split(SPLIT_CHAR).map((s, index) => (
                            <LocationContainer key={index}>
                                <Text variant="body2">{s}</Text>
                            </LocationContainer>
                        ))}
                    </>
                ) : (
                    <Text variant="body2">All Locations</Text>
                ),
        },
        {
            key: 'permissions',
            label: 'Permissions',
            render: (row) =>
                row.permissions ? (
                    <>
                        {row.permissions.split(SPLIT_CHAR).map((x) => (
                            <Chip key={x} label={x} style={{ margin: 5 }} />
                        ))}
                    </>
                ) : null,
        },
        {
            key: 'roles',
            label: 'Roles',
            render: (row) =>
                row.roles ? (
                    <>
                        {row.roles.split(SPLIT_CHAR).map((x) => (
                            <Chip key={x} label={x} style={{ margin: 5 }} />
                        ))}
                    </>
                ) : null,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) =>
                row.hideActions ? null : (
                    <div>
                        <TextButton
                            onClick={() => setSelectedContact(row)}
                            buttonStyle={{ width: 'fit-content', display: 'inline' }}
                        >
                            Edit
                        </TextButton>
                        |
                        <TextButton
                            onClick={() => setContactToDelete(row)}
                            buttonStyle={{ width: 'fit-content', display: 'inline' }}
                        >
                            Delete
                        </TextButton>
                    </div>
                ),
        },
    ];

    return (
        <Grid container direction="column" justifyContent="center">
            <Grid container item xs={12} direction="row" justifyContent="space-between" alignItems="center">
                <Text variant="display">Manage Users</Text>
                {isLoading && <CircularProgress size={20} />}
                <AddUser onContactAdded={onContactAdded} />
            </Grid>
            <TableWrapper>
                <Table
                    columns={columns}
                    data={tableData}
                    page={pageNumber}
                    count={contacts?.count ?? 0}
                    perPage={contacts?.per_page ?? 25}
                    onPageChange={onPageChange}
                />
            </TableWrapper>
            <EditUser
                isModalOpen={Boolean(selectedContact)}
                onClose={() => setSelectedContact(undefined)}
                userId={selectedContact?.user}
                initialLocations={selectedContact?.locations ? selectedContact?.locations.split(SPLIT_CHAR) : []}
                initialPermissions={selectedContact?.permissions.split(SPLIT_CHAR)}
                initialRoles={selectedContact?.roles.split(SPLIT_CHAR)}
                onUserUpdated={() => {
                    setSelectedContact(undefined);
                    loadContacts(pageNumber);
                }}
                name={`${selectedContact?.first_name} ${selectedContact?.last_name}`}
            />
            <ConfirmDialog
                title="Confirm Delete"
                message="Are you sure you want to delete this contact?"
                open={Boolean(contactToDelete)}
                onClose={() => setContactToDelete(undefined)}
                onConfirm={onDeleteContact}
            />
        </Grid>
    );
}

const TableWrapper = styled('div')({
    marginTop: 10,
    marginBottom: 40,
});

const LocationContainer = styled('div')({
    paddingBottom: 3,
});
